import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchCalendarEvents } from '~/features/market/calendar/api';
import { CalendarEvent } from '~/features/market/calendar/types';
import {
  EVENT_TYPE_LABELS,
  addDays,
  formatDisplayDate,
  getStatusLevel,
  parseISODate,
  toISODate,
} from '~/features/market/calendar/utils';
import { paths } from '~/routing';
import { getHlxNavIcon } from '~/routing/hlxNavIcons';

const findNext = (events: CalendarEvent[], type: CalendarEvent['eventType']) =>
  events.find((e) => e.eventType === type);

// [MCV-1516] Match the left sidebar's icon for this destination.
export const CalendarIcon = getHlxNavIcon(paths.hlx.calendar) ?? CalendarDaysIcon;

/** Dashboard module summarizing operational dates over the next 30 days. */
export const UpcomingCalendarCard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const today = new Date();
    fetchCalendarEvents(toISODate(today), toISODate(addDays(today, 30)))
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err) => {
        console.debug('UpcomingCalendarCard: failed to load events', err);
        if (!cancelled) setEvents(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = useMemo(() => {
    if (!events) return null;
    const today = new Date();
    const in7 = toISODate(addDays(today, 7));
    const starts7 = events.filter(
      (e) => e.eventType.endsWith('_START') && e.eventDate <= in7
    ).length;
    const endings30 = events.filter((e) => e.eventType.endsWith('_END')).length;
    const alerts = events.filter((e) =>
      ['warning', 'blocked'].includes(getStatusLevel(e, today))
    ).length;
    const highlights = [
      findNext(events, 'CONTRACT_START'),
      findNext(events, 'CONTRACT_END'),
      findNext(events, 'LISTING_START'),
      findNext(events, 'LISTING_END'),
      findNext(events, 'B2B_CONTRACT_START'),
      findNext(events, 'B2B_CONTRACT_END'),
    ].filter((e): e is CalendarEvent => !!e);
    return { starts7, endings30, alerts, highlights };
  }, [events]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 animate-pulse">
        <div className="h-5 bg-grey-900 rounded w-1/3 mb-4" />
        <div className="h-24 bg-grey-900 rounded" />
      </div>
    );
  }

  if (!summary) return null;

  return (
    <button
      type="button"
      className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 w-full text-left hover:shadow-md transition-shadow"
      onClick={() => navigate(paths.hlx.calendar)}
      data-cy="upcoming-calendar-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <CalendarIcon className="size-5 text-primary-50" />
        <h3 className="sub-heading-sb text-grey-100">Upcoming Calendar (30 days)</h3>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-2xl font-bold text-primary-50">{summary.starts7}</p>
          <p className="body-text text-grey-500">Starts in 7 days</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-warning">{summary.endings30}</p>
          <p className="body-text text-grey-500">Endings in 30 days</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-danger">{summary.alerts}</p>
          <p className="body-text text-grey-500">Alerts</p>
        </div>
      </div>
      {summary.highlights.length > 0 ? (
        <ul className="border-t border-grey-900 pt-3 flex flex-col gap-1.5">
          {summary.highlights.map((e) => (
            <li key={e.eventId} className="flex justify-between gap-3 text-sm">
              <span className="truncate text-grey-100">
                <span className="text-grey-500">{EVENT_TYPE_LABELS[e.eventType]}: </span>
                {e.title}
                {e.renewable && (
                  <span className="ml-1 rounded bg-purple-100 px-1 text-[10px] font-semibold uppercase text-purple-800">
                    Renewable
                  </span>
                )}
              </span>
              <span className="shrink-0 text-grey-500">
                {parseISODate(e.eventDate).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="border-t border-grey-900 pt-3 text-sm text-grey-500">
          No upcoming dates in the next 30 days. Next event:{' '}
          {events && events[0] ? formatDisplayDate(events[0].eventDate) : 'none scheduled'}.
        </p>
      )}
    </button>
  );
};
